import axios from "axios";
import moment from "moment";
import PartnersTable from "./PartnersTable";
import React, { Component } from "react";
import { DateRangePicker } from "react-dates";
import { connect } from "react-redux";
import {
  Theme,
  createStyles,
  withStyles,
  WithStyles,
  Button,
  Box,
  Typography,
  Grid,
  TextField,
  IconButton,
  TablePagination,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Tab,
  Tabs,
  DialogActions,
  Menu,
  MenuItem,
  Divider,
} from "@material-ui/core";
import InputAdornment from "@material-ui/core/InputAdornment";
import { NavigateNext, NavigateBefore, Clear } from "@material-ui/icons";
import { URL } from "../../config/config";
import AddIcon from "@material-ui/icons/AddOutlined";
import AddPartner from "./AddPartner";
import DownloadIcon from "@material-ui/icons/GetApp";
import CloseIcon from "@material-ui/icons/Close";
import { Menu as MenuIcon } from "lucide-react";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { SnackbarContext } from "../../commons/Snackbar/Snackbar";

/**
 * @typedef {import("../../types").Partner} Partner
 * @typedef {import("../../types").PartnersResponse} PartnersResponse
 * @typedef {import("axios").AxiosResponse<PartnersResponse>} AxiosPartnersResponse
 */

const createInitialState = () => ({
  rowsPerPage: 15,
  hasMore: true,
  skip: 0,
  timeStamp: new Date(),
  search: "",
  /** @type {Types.Partner[]} */
  partners: [],
  err: null,
  isLoading: false,
  page: 0,
  applied: {
    createdAt: {
      startDate: null,
      endDate: null,
      focus: null,
    },
    dateRange: {
      startDate: null,
      endDate: null,
      focus: null,
    },
  },
  openAddPartnerDialog: false,
  openPartnerReportDialog: false,
  successPartnersData: true,
  percentageFetchedData: 0,
  tabValue: 0,
  whitelabelsCount: 0,
  adsWhitelabelsCount: 0,
  whitelabels: [],
  direct: [],
  adsWhitelabels: [],
  successPartnersDataConsolidated: true,
  menuOpen: false,
  menuAnchorEl: null,
  formLink: "",
  formId: "",
  formDialogOpen: false,
});

/**
 * @typedef {WithStyles<typeof styles>} Props
 * @typedef {ReturnType<typeof createInitialState>} State
 */

/**
 * @extends Component<Props, State>
 */
class PartnersPage extends Component {
  constructor(props) {
    super(props);
    this.state = createInitialState();
    this.intervalId = null;
  }

  static contextType = SnackbarContext;

  getVisibleTabs = () => {
    const { permissions } = this.props;
    const partnersPermission = permissions.find((p) => p.label === "partners");
    if (!partnersPermission || !Array.isArray(partnersPermission.subroutes))
      return [];
    // Map label to display name and partner type
    const labelMap = {
      whitelabels: { display: "WHITELABELS", partnerType: "WHITELABEL" },
      direct: { display: "DIRECT", partnerType: "DIRECT" },
      adsWhitelabel: {
        display: "ADS_WHITELABELS",
        partnerType: "ADS_WHITELABEL",
      },
    };
    return partnersPermission.subroutes
      .filter((sr) => sr.read)
      .map((sr) => ({
        label: sr.label,
        display: labelMap[sr.label]?.display || sr.label,
        partnerType: labelMap[sr.label]?.partnerType,
        write: sr.write,
        actions: sr.actions,
        subroutes: sr.subroutes,
      }));
  };

  // Helper to get the current tab info
  getCurrentTab = () => {
    const visibleTabs = this.getVisibleTabs();
    return visibleTabs[this.state.tabValue];
  };

  hasWriteAccess = () => {
    const visibleTabs = this.getVisibleTabs();
    return visibleTabs[this.state.tabValue]?.write;
  };

  componentDidMount() {
    // Ensure tabValue is valid (in case permissions change)
    const visibleTabs = this.getVisibleTabs();
    if (visibleTabs.length === 0) return; // No tabs to show
    if (this.state.tabValue >= visibleTabs.length) {
      this.setState({ tabValue: 0 }, this.fetchPartners);
    } else {
      this.fetchPartners();
    }
  }

  closeAddPartnerDialog = () => this.setState({ openAddPartnerDialog: false });

  closePartnerReportDialog = () =>
    this.setState({ openPartnerReportDialog: false });

  onAddedPartnerDialog = () => {
    this.closeAddPartnerDialog();
    this.setState(createInitialState(), () => {
      this.fetchPartners();
    });
  };

  openAddPartnerDialog = () => this.setState({ openAddPartnerDialog: true });

  openPartnerReportDialog = () =>
    this.setState({ openPartnerReportDialog: true });

  calculateEstimatedTime = () => {
    const { totalCount, successPartnersData } = this.state;
    let secondsPassed = 0;

    this.intervalId = setInterval(() => {
      secondsPassed++;
      this.setState({
        percentageFetchedData: (secondsPassed / totalCount) * 100,
      });
      if (secondsPassed >= totalCount || !successPartnersData) {
        clearInterval(this.intervalId);
      }
    }, 1000);
  };

  formatDate = (date) => {
    const months = [
      "JANUARY",
      "FEBRUARY",
      "MARCH",
      "APRIL",
      "MAY",
      "JUNE",
      "JULY",
      "AUGUST",
      "SEPTEMBER",
      "OCTOBER",
      "NOVEMBER",
      "DECEMBER",
    ];

    const day = String(date._d.getDate()).padStart(2, "0");
    const month = months[date._d.getMonth()];
    const year = date._d.getFullYear();

    return `${day}_${month}_${year}`;
  };

  getPartnersAggregatedReport = (e, consolidated = false) => {
    const { dateRange } = this.state.applied;
    const startDate = dateRange.startDate;
    const endDate = dateRange.endDate;
    consolidated
      ? this.setState({ successPartnersDataConsolidated: false })
      : this.setState({ successPartnersData: false });
    this.calculateEstimatedTime();
    let sd, ed;
    if (startDate) sd = this.formatDate(startDate);
    if (endDate) ed = this.formatDate(endDate);
    axios
      .post(
        consolidated
          ? `${URL}/superadmin/reporting/partners/consolidated-billing-report`
          : `${URL}/superadmin/partners-aggregated-report`,
        { startDate, endDate },
        { ...(consolidated ? {} : { responseType: "stream" }) }
      )
      .then((res) => {
        const cbLogs = res.data?.data;
        if (!cbLogs || !cbLogs.length) {
          console.error("No data available to generate CSV.");
          return "failure";
        }
        // Function to flatten JSON
        const flattenObject = (obj, parent = "", res = {}) => {
          for (let key in obj) {
            let propName = parent ? `${parent}/${key}` : key;
            if (typeof obj[key] === "object" && obj[key] !== null) {
              flattenObject(obj[key], propName, res);
            } else {
              res[propName] = obj[key];
            }
          }
          return res;
        };
        // Flatten all rows
        const flattenedData = cbLogs.map((row) => flattenObject(row));
        // Get all headers from the flattened data
        const headers = Array.from(
          flattenedData.reduce((acc, row) => {
            Object.keys(row).forEach((key) => acc.add(key));
            return acc;
          }, new Set())
        );
        // Create CSV content
        const replacer = (key, value) => (value === null ? "" : value);
        const csv = [
          headers.join(","), // CSV header row
          ...flattenedData.map((row) =>
            headers
              .map((fieldName) => JSON.stringify(row[fieldName], replacer))
              .join(",")
          ),
        ].join("\r\n");
        // Download the file as CSV
        var downloadLink = document.createElement("a");
        var blob = new Blob(["\ufeff", csv]);
        var url = window.URL.createObjectURL(blob);
        downloadLink.href = url;
        downloadLink.download =
          sd && ed
            ? "PARTNERS_STATS_" + `${sd}-${ed}` + ".csv"
            : "PARTNERS_STATS_ALL" + ".csv";
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        consolidated
          ? this.setState({ successPartnersDataConsolidated: true })
          : this.setState({ successPartnersData: true });
      })
      .catch((error) => {
        console.error(error);
        this.setState({
          status: "error",
          statusMessage: "Unable to fetch Data",
          loading: false,
          successPartnersDataConsolidated: true,
          successPartnersData: true,
        });
      });
  };

  fetchPartners = async (fromTabChange = false) => {
    this.setState({ isLoading: true, err: null });
    try {
      const t = this.state;
      let filter = {};
      if (t.search) {
        filter["search"] = t.search;
      }
      if (t.applied.createdAt.startDate) {
        filter["createdAt"] = {
          $gte: t.applied.createdAt.startDate,
          $lte: t.applied.createdAt.endDate,
        };
      }
      // Use mapped partner type instead of hardcoded tab values
      const currentTab = this.getCurrentTab();
      const partnerType = currentTab?.partnerType || "WHITELABEL";

      if (
        t.search ||
        t.applied.createdAt.startDate ||
        t.applied.createdAt.endDate
      ) {
        filter.type = { $in: ["WHITELABEL", "DIRECT", "ADS_WHITELABEL"] };
      } else filter.type = partnerType;

      const queryObj = {
        skip: t.skip,
        timeStamp: t.timeStamp,
        rowsPerPage: t.rowsPerPage,
        search: "",
        allFilters: JSON.stringify({ ...filter }),
      };

      /** @type {AxiosPartnersResponse} */
      const response = await axios.get(`${URL}/superadmin/get-partners`, {
        params: queryObj,
      });
      const { newSkip, partners } = response.data;
      const hasMore = partners.length >= this.state.rowsPerPage;

      this.setState({
        skip: newSkip,
        partners: [...this.state.partners, ...partners],
        hasMore,
        isLoading: false,
      });

      const whitelabels = this.state.partners.filter(
        (partner) => partner.type === "WHITELABEL"
      );
      const direct = this.state.partners.filter(
        (partner) => partner.type === "DIRECT"
      );
      const adsWhitelabels = this.state.partners.filter(
        (partner) => partner.type === "ADS_WHITELABEL"
      );

      // Update tab switching logic to use visible tabs
      if (!fromTabChange) {
        const visibleTabs = this.getVisibleTabs();
        const currentTabIndex = this.state.tabValue;
        const currentTab = visibleTabs[currentTabIndex];

        if (currentTab) {
          const currentPartnerType = currentTab.partnerType;
          let hasData = false;

          switch (currentPartnerType) {
            case "WHITELABEL":
              hasData = whitelabels?.length > 0;
              break;
            case "DIRECT":
              hasData = direct?.length > 0;
              break;
            case "ADS_WHITELABEL":
              hasData = adsWhitelabels?.length > 0;
              break;
          }

          // If current tab has no data, switch to first available tab with data
          if (!hasData) {
            for (let i = 0; i < visibleTabs.length; i++) {
              const tab = visibleTabs[i];
              let tabHasData = false;

              switch (tab.partnerType) {
                case "WHITELABEL":
                  tabHasData = whitelabels?.length > 0;
                  break;
                case "DIRECT":
                  tabHasData = direct?.length > 0;
                  break;
                case "ADS_WHITELABEL":
                  tabHasData = adsWhitelabels?.length > 0;
                  break;
              }

              if (tabHasData) {
                this.setState({ tabValue: i });
                break;
              }
            }
          }
        }
      }

      this.setState({
        directCount: partners.length,
        direct: direct,
        whitelabelsCount: whitelabels.length,
        whitelabels: whitelabels,
        adsWhitelabelsCount: adsWhitelabels.length,
        adsWhitelabels: adsWhitelabels,
      });
    } catch (err) {
      console.error("Fetch partners error:", err);
      this.setState({
        err,
        isLoading: false,
        hasMore: false,
      });
    }
  };

  /** @param {Partner} partner */
  setPartner = (partner) => {
    const partners = [...this.state.partners];
    const foundIndex = partners.findIndex((x) => x._id == partner._id);
    partners[foundIndex] = partner;
    const whitelabels = partners.filter(
      (partner) => partner.type === "WHITELABEL"
    );
    const direct = partners.filter((partner) => partner.type === "DIRECT");
    const adsWhitelabels = partners.filter(
      (partner) => partner.type === "ADS_WHITELABEL"
    );
    this.setState({
      directCount: direct.length,
      direct: direct,
      whitelabelsCount: whitelabels.length,
      whitelabels: whitelabels,
      adsWhitelabelsCount: adsWhitelabels.length,
      adsWhitelabels: adsWhitelabels,
      partners: partners,
    });
  };

  getPageRows = () => {
    const {
      partners,
      page,
      rowsPerPage,
      tabValue,
      whitelabels,
      direct,
      adsWhitelabels,
    } = this.state;

    // Use mapped partner type instead of hardcoded tab values
    const currentTab = this.getCurrentTab();
    const partnerType = currentTab?.partnerType;

    let currentPartners;
    switch (partnerType) {
      case "WHITELABEL":
        currentPartners = whitelabels;
        break;
      case "DIRECT":
        currentPartners = direct;
        break;
      case "ADS_WHITELABEL":
        currentPartners = adsWhitelabels;
        break;
      default:
        currentPartners = whitelabels; // fallback
    }

    return currentPartners.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  };

  handleChangePage = (event, newPage) => {
    const {
      partners,
      page,
      rowsPerPage,
      whitelabels,
      direct,
      adsWhitelabels,
      tabValue,
    } = this.state;

    // Use mapped partner type instead of hardcoded tab values
    const currentTab = this.getCurrentTab();
    const partnerType = currentTab?.partnerType;

    let currentPartners;
    switch (partnerType) {
      case "WHITELABEL":
        currentPartners = whitelabels;
        break;
      case "DIRECT":
        currentPartners = direct;
        break;
      case "ADS_WHITELABEL":
        currentPartners = adsWhitelabels;
        break;
      default:
        currentPartners = whitelabels; // fallback
    }

    const step = newPage - page;
    if (step === 1) {
      const pageRows = currentPartners.slice(
        newPage * rowsPerPage,
        newPage * rowsPerPage + rowsPerPage
      );
      if (!pageRows.length) {
        this.fetchPartners();
      }
    }
    this.setState({ page: newPage });
  };

  handleInput = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };
  handleEnter = (e) => {
    if (e.key == "Enter") {
      this.setState(
        {
          timeStamp: new Date(),
          skip: 0,
          page: 0,
          hasMore: true,
          partners: [],
          isLoading: true,
          err: null,
        },
        () => {
          this.fetchPartners();
        }
      );
    }
  };
  // methods for date filter
  clearDate = (filterKey) => {
    const applied = { ...this.state.applied };
    applied[filterKey].startDate = null;
    applied[filterKey].endDate = null;
    this.setState({ applied });
  };
  checkDateInput = (filterKey) => {
    const applied = { ...this.state.applied };
    const s = applied[filterKey];
    if (s.startDate && s.endDate) {
      return;
    } else {
      this.clearDate(filterKey);
    }
  };
  setFocus = (filterKey, focusedInput) => {
    const applied = { ...this.state.applied };
    applied[filterKey].focusedInput = focusedInput;
    this.setState({ applied });
  };
  setDate = (filterKey, startDate, endDate) => {
    const applied = { ...this.state.applied };
    applied[filterKey].startDate = startDate;
    applied[filterKey].endDate = endDate;
    this.setState({ applied });
  };
  applyFilter = () => {
    this.setState(
      {
        timeStamp: new Date(),
        skip: 0,
        page: 0,
        hasMore: true,
        partners: [],
        isLoading: true,
        err: null,
      },
      () => {
        this.fetchPartners();
      }
    );
  };

  handleTabs = (event, newValue) => {
    this.setState(
      {
        timeStamp: new Date(),
        skip: 0,
        page: 0,
        hasMore: true,
        partners: [],
        isLoading: true,
        err: null,
        value: newValue,
      },
      () => {
        this.fetchPartners(true);
      }
    );
    this.setState({ tabValue: newValue, page: 0 });
  };

  handleGenerateFormLink = async (e) => {
    try {
      const { data } = await axios.post(
        `${URL}/superadmin/generate-partner-form-link`
      );
      const { formLink, formId } = data;
      this.setState({ formLink, formId, formDialogOpen: true });
    } catch (e) {
      console.error(e);
    }
  };

  closeFormDialog = (e) => {
    this.setState({ formDialogOpen: false });
  };

  handleCopy = async (text, label) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      this.context?.showSnackbar?.(`${label} copied to clipboard`, "success");
    } catch (err) {
      console.error("Copy failed", err);
      this.context?.showSnackbar?.(`Failed to copy ${label}`, "error");
    }
  };

  render() {
    const { classes, agent, permissions } = this.props;
    const { tabValue, menuOpen } = this.state;
    const visibleTabs = this.getVisibleTabs();

    // if (visibleTabs.length === 0) {
    //   return <div>No access to any partner types.</div>;
    // }

    const partnersPermission = permissions.find((p) => p.label === "partners");
    const reportPermission = partnersPermission?.actions?.includes("report");
    const addPartnerPermission =
      partnersPermission?.actions?.includes("addPartner");
    const generateFormLinkPermission = partnersPermission?.actions?.includes(
      "generatePartnerForm"
    );

    return (
      <div className={classes.root}>
        <Grid
          container
          className={`${classes.pageTitleContainer} ${classes.fullWidth}`}
          spacing={2}
        >
          <Grid item>
            <Typography variant="h3">Partners</Typography>
          </Grid>
          <Grid item>
            <TextField
              name="search"
              placeholder="Search Partners"
              className={classes.textField}
              onChange={this.handleInput}
              onKeyPress={this.handleEnter}
            />
          </Grid>
          <Grid item>
            <DateFilter
              filterKey="createdAt"
              filterName="Created At"
              classes={classes}
              applied={this.state.applied}
              setDate={this.setDate}
              setFocus={this.setFocus}
              checkDateInput={this.checkDateInput}
              clearDate={this.clearDate}
            />
          </Grid>
          <Grid item>
            <Button
              color="primary"
              variant="contained"
              onClick={this.applyFilter}
            >
              Apply
            </Button>
          </Grid>
          <Grid item xs></Grid>
          <Grid item>
            <IconButton
              color="primary"
              onClick={(e) =>
                this.setState((prev) => ({
                  menuOpen: !prev.menuOpen,
                  menuAnchorEl: prev.menuOpen ? null : e.currentTarget,
                }))
              }
            >
              {menuOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
            <Menu
              open={menuOpen}
              onClose={() =>
                this.setState({ menuOpen: false, menuAnchorEl: null })
              }
              anchorEl={this.state.menuAnchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              style={{ marginTop: "40px" }}
            >
              {!generateFormLinkPermission ? (
                <></>
              ) : (
                <MenuItem onClick={this.handleGenerateFormLink}>
                  Generate Form Link
                </MenuItem>
              )}
              {!reportPermission ? (
                <></>
              ) : (
                <MenuItem onClick={this.openPartnerReportDialog}>
                  Report
                </MenuItem>
              )}
              {!addPartnerPermission ? (
                <></>
              ) : (
                <MenuItem onClick={this.openAddPartnerDialog}>
                  Add Partner
                </MenuItem>
              )}
            </Menu>
          </Grid>
        </Grid>
        <Box my={3} />
        <Grid container justify="center">
          <Grid item xs={12} md={11} lg={10}>
            {/* First section */}
            {/* Scrollable (x & y) table container */}
            {visibleTabs.length === 0 ? (
              <div>No access to any partner types.</div>
            ) : (
              <div className={classes.tableContainer}>
                <Tabs
                  value={tabValue}
                  onChange={this.handleTabs}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="fullWidth"
                >
                  {visibleTabs.map((tab, idx) => (
                    <Tab key={tab.label} label={tab.display} />
                  ))}
                </Tabs>
                {visibleTabs.map(
                  (tab, idx) =>
                    tabValue === idx && (
                      <PartnersTable
                        key={tab.label}
                        partners={this.getPageRows()}
                        isLoading={this.state.isLoading}
                        setPartner={this.setPartner}
                        permissions={tab}
                      />
                    )
                )}
              </div>
            )}
          </Grid>
        </Grid>

        {/* Table footer fixed at bottom */}
        <div className={classes.fixedBottomContainer}>
          {visibleTabs.map(
            (tab, idx) =>
              tabValue === idx && (
                <TablePagination
                  key={tab.label}
                  component="div"
                  count={(() => {
                    // Get the correct count based on partner type
                    switch (tab.partnerType) {
                      case "WHITELABEL":
                        return (
                          this.state.whitelabels.length +
                          (this.state.hasMore ? this.state.rowsPerPage : 0)
                        );
                      case "DIRECT":
                        return (
                          this.state.direct.length +
                          (this.state.hasMore ? this.state.rowsPerPage : 0)
                        );
                      case "ADS_WHITELABEL":
                        return (
                          this.state.adsWhitelabels.length +
                          (this.state.hasMore ? this.state.rowsPerPage : 0)
                        );
                      default:
                        return 0;
                    }
                  })()}
                  page={this.state.page}
                  onPageChange={this.handleChangePage}
                  rowsPerPage={this.state.rowsPerPage}
                  rowsPerPageOptions={[25]}
                />
              )
          )}
        </div>
        <Dialog open={this.state.openAddPartnerDialog}>
          <AddPartner
            onClose={this.closeAddPartnerDialog}
            onSuccess={this.onAddedPartnerDialog}
          />
        </Dialog>
        <Dialog
          open={this.state.openPartnerReportDialog}
          onClose={this.closePartnerReportDialog}
        >
          <DialogTitle>
            <Grid
              container
              alignItems="center"
              spacing={2}
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <Grid item>
                <Typography variant="h3" component="p">
                  Partners Report
                </Typography>
              </Grid>
              <Grid item>
                <IconButton onClick={this.closePartnerReportDialog}>
                  <CloseIcon />
                </IconButton>
              </Grid>
            </Grid>
          </DialogTitle>
          <DialogContent>
            <Typography variant="h5">
              Select the date range to download partners report
            </Typography>
            <br></br>
            <Grid
              container
              direction="column"
              className={classes.content}
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <DateFilter
                filterKey="dateRange"
                filterName="Date Range"
                classes={classes}
                applied={this.state.applied}
                setDate={this.setDate}
                setFocus={this.setFocus}
                checkDateInput={this.checkDateInput}
                clearDate={this.clearDate}
              />
            </Grid>
          </DialogContent>
          <DialogActions style={{ margin: "10px" }}>
            <Button
              color="primary"
              variant="contained"
              endIcon={
                this.state.successPartnersData ? (
                  <DownloadIcon />
                ) : (
                  <CircularProgress
                    variant="determinate"
                    size={20}
                    value={this.state.percentageFetchedData}
                  />
                )
              }
              disabled={!this.state.successPartnersData}
              onClick={this.getPartnersAggregatedReport}
              style={{
                width: "180px",
                height: "33px",
              }}
            >
              Download Report
            </Button>
            <Button
              color="primary"
              variant="contained"
              endIcon={
                this.state.successPartnersDataConsolidated ? (
                  <DownloadIcon />
                ) : (
                  <CircularProgress variant="determinate" size={20} />
                )
              }
              disabled={!this.state.successPartnersDataConsolidated}
              onClick={(e) => this.getPartnersAggregatedReport(e, true)}
              style={{
                width: "180px",
                height: "33px",
              }}
            >
              Consolidated
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={this.state.formDialogOpen} onClose={this.closeFormDialog}>
          <DialogTitle style={{ padding: "8px 24px" }}>
            <Grid
              container
              alignItems="center"
              // spacing={2}
              style={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Grid item>
                <Typography variant="h3" component="p">
                  Generated form
                </Typography>
              </Grid>
              <Grid item>
                <IconButton onClick={this.closeFormDialog}>
                  <CloseIcon />
                </IconButton>
              </Grid>
            </Grid>
          </DialogTitle>
          <Divider />
          <DialogContent>
            <Grid
              container
              spacing={2}
              direction="column"
              style={{ width: "480px", padding: "16px 0px" }}
            >
              <Grid item>
                <Typography gutterBottom variant="h4">
                  Form Link
                </Typography>
                <TextField
                  value={this.state.formLink}
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="copy form link"
                          onClick={() =>
                            this.handleCopy(this.state.formLink, "Form link")
                          }
                        >
                          <FileCopyIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item>
                <Typography gutterBottom variant="h4">
                  Form Id
                </Typography>
                <TextField
                  value={this.state.formId}
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="copy form id"
                          onClick={() =>
                            this.handleCopy(this.state.formId, "Form ID")
                          }
                        >
                          <FileCopyIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
}

function NavigationWrapper(props) {
  const { classes } = props;
  return (
    <div className={classes.navigationWrapper}>
      <IconButton size="small">{props.children}</IconButton>
    </div>
  );
}

function DateFilter({
  filterKey,
  applied,
  setFocus,
  setDate,
  checkDateInput,
  clearDate,
  ...props
}) {
  const { classes, filterName } = props;
  const A = applied[filterKey];
  return (
    <Box className={classes.filterTypeContainer}>
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        {A.startDate && A.endDate ? (
          <Grid item>
            <IconButton
              color="primary"
              size="small"
              variant="filled"
              onClick={() => clearDate(filterKey)}
            >
              <Clear />
            </IconButton>
          </Grid>
        ) : (
          ""
        )}
        <Grid item>
          <Typography variant="body1">{filterName}</Typography>
        </Grid>
        <Grid item xs>
          <DateRangePicker
            startDatePlaceholderText="From"
            endDatePlaceholderText="To"
            onClose={() => checkDateInput(filterKey)}
            startDateId="startDate"
            endDateId="endDate"
            startDate={A.startDate}
            endDate={A.endDate}
            onDatesChange={({ startDate, endDate }) =>
              setDate(filterKey, startDate, endDate)
            }
            focusedInput={A.focusedInput}
            onFocusChange={(focusedInput) => setFocus(filterKey, focusedInput)}
            navPosition="navPositionTop"
            numberOfMonths={1}
            navPrev={
              <NavigationWrapper classes={classes}>
                <NavigateBefore />
              </NavigationWrapper>
            }
            navNext={
              <NavigationWrapper classes={classes}>
                <NavigateNext />
              </NavigationWrapper>
            }
            hideKeyboardShortcutsPanel
            customArrowIcon={null}
            screenReaderInputMessage={" "}
            small
            readOnly
            isOutsideRange={(day) => moment().diff(day) < 0}
            minimumNights={0}
          />
        </Grid>
      </Grid>
    </Box>
  );

  // function setToday()
}

/** @param {Theme} theme */
const styles = (theme) =>
  createStyles({
    root: {
      width: "100%",
      height: "100%",
      background: "rgb(249,249,249)",
      boxSizing: "border-box",
      overflowX: "hidden",
      position: "relative",
    },
    pageTitleContainer: {
      position: "sticky",
      zIndex: 100,
      top: 0,
      height: 60,
      boxSizing: "border-box",
      alignItems: "center",
      padding: "0 60px",
      [theme.breakpoints.down("md")]: {},
      [theme.breakpoints.down("sm")]: {},
    },
    fullWidth: {
      background: "white",
    },
    container: {
      background: "white",
      borderRadius: "8px",
    },
    fixedBottomContainer: {
      position: "fixed",
      height: "60px",
      borderTop: "1px solid lightgrey",
      background: "white",
      bottom: "0",
      left: "71px",
      right: "0",
      overflow: "hidden",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      [theme.breakpoints.down("sm")]: {
        left: "0px",
        height: "50px",
      },
    },
    tableContainer: {
      top: "60px",
      height: "calc(100vh - 70px - 60px - 40px)",
      width: "100%",
      overflow: "hidden",
      boxSizing: "border-box",
      position: "sticky",
      [theme.breakpoints.down("sm")]: {
        height: "calc(100vh - 70px - 50px - 10px - 50px)",
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        boxSizing: "border-box",
      },
    },
    textField: {
      width: 300,
    },
    content: {
      width: "70vw",
      minHeight: "400px",
      maxWidth: "600px",
      display: "flex",
      flexDirection: "row",
    },
  });

const connectedPartnersPage = connect((state) => ({
  agent: state.login.user,
  permissions: state.login.permissions,
}))(PartnersPage);

export default withStyles(styles)(connectedPartnersPage);
